import React, { useState, useEffect, useCallback, useMemo } from 'react';
import articlesData from './ArticlesData';
import conferencesImage from '../assets/actualitedelaamtdm/conferences20262027.jpg';
import conferencesPdf from '../assets/actualitedelaamtdm/conferences20262027.pdf';
import '../styles/ActualitesAAMTDM.css';

const ActualitesAAMTDM = () => {
    const [currMonth, setCurrMonth] = useState(new Date().getMonth());
    const [currYear, setCurrYear] = useState(new Date().getFullYear());
    const [openYear, setOpenYear] = useState(null);
    const [pdfViewerVisible, setPdfViewerVisible] = useState(false);
    const [selectedPdfPath, setSelectedPdfPath] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    const months = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

    // --- LOGIQUE DE DATES ---
    const stringToDate = useCallback((dateStr) => {
        if (!dateStr) return new Date();
        const monthNames = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
        const parts = dateStr.toLowerCase().trim().split(' ');
        const year = parseInt(parts.find(p => p.length === 4 && !isNaN(p)), 10);
        const monthIndex = monthNames.findIndex(m => parts.includes(m));
        const day = parseInt(parts.find(p => !isNaN(p) && p.length <= 2), 10);
        return new Date(year, monthIndex, day);
    }, []);

    const parseDatesRange = useCallback((dateRangeStr) => {
        const lowerStr = dateRangeStr.toLowerCase();
        if (lowerStr.includes(' au ')) {
            const segments = lowerStr.split(' au ');
            const startDate = stringToDate(segments[0]);
            const endDate = stringToDate(segments[1]);
            if (isNaN(startDate.getFullYear()) || startDate.getFullYear() < 2000) {
                startDate.setFullYear(endDate.getFullYear());
            }
            return { start: startDate, end: endDate };
        }
        const singleDate = stringToDate(dateRangeStr);
        return { start: singleDate, end: singleDate };
    }, [stringToDate]);

    // --- REPARTITION DES DONNEES ---
    const articlesByDate = useMemo(() => {
        const map = {};
        articlesData.forEach(article => {
            const { start } = parseDatesRange(article.date);
            const key = `${start.getFullYear()}-${start.getMonth()}-${start.getDate()}`;
            if (!map[key]) map[key] = [];
            map[key].push(article);
        });
        return map;
    }, [parseDatesRange]);

    const archivedByYear = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return articlesData
            .filter(article => article.category === "association")
            .map(article => ({ ...article, parsed: parseDatesRange(article.date) }))
            .filter(article => {
                const end = new Date(article.parsed.end);
                end.setHours(23, 59, 59, 999);
                return end < today;
            })
            .sort((a, b) => b.parsed.start - a.parsed.start)
            .reduce((acc, article) => {
                const year = article.parsed.end.getFullYear();
                if (!acc[year]) acc[year] = [];
                acc[year].push(article);
                return acc;
            }, {});
    }, [parseDatesRange]);

    const sortedYears = useMemo(() => Object.keys(archivedByYear).sort((a, b) => b - a), [archivedByYear]);

    // --- EFFETS ET NAVIGATION ---
    useEffect(() => {
        const checkIsMobile = () => setIsMobile(window.innerWidth <= 768);
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => window.removeEventListener('resize', checkIsMobile);
    }, []);

    const scrollNewsGrid = (direction, elementId) => {
        const grid = document.getElementById(elementId);
        if (grid) {
            const scrollAmount = isMobile ? window.innerWidth * 0.8 : 324;
            grid.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
        }
    };

    const handleMonthChange = (direction) => {
        let newMonth = currMonth + direction;
        let newYear = currYear;
        if (newMonth < 0) { newMonth = 11; newYear--; }
        else if (newMonth > 11) { newMonth = 0; newYear++; }
        setCurrMonth(newMonth);
        setCurrYear(newYear);
    };

    const openPdfViewer = (pdfPath) => {
        if (isMobile) { window.open(pdfPath, '_blank'); } 
        else { 
            setSelectedPdfPath(pdfPath); 
            setPdfViewerVisible(true); 
            document.body.style.overflow = 'hidden'; 
        }
    };

    const closePdfViewer = () => {
        setPdfViewerVisible(false);
        document.body.style.overflow = 'unset';
    };

    const renderCalendar = () => {
        const firstDayofMonth = new Date(currYear, currMonth, 1).getDay() || 7;
        const lastDateofMonth = new Date(currYear, currMonth + 1, 0).getDate();
        const lastDayofMonth = new Date(currYear, currMonth, lastDateofMonth).getDay();
        const lastDateofLastMonth = new Date(currYear, currMonth, 0).getDate();

        let liTags = [];
        for (let i = firstDayofMonth - 1; i > 0; i--) {
            liTags.push(<li key={`prev-${i}`} className="inactive">{lastDateofLastMonth - i + 1}</li>);
        }
        for (let i = 1; i <= lastDateofMonth; i++) {
            const isToday = i === new Date().getDate() && currMonth === new Date().getMonth() && currYear === new Date().getFullYear();
            const dateKey = `${currYear}-${currMonth}-${i}`;
            const hasEvent = articlesByDate[dateKey] ? "event" : "";
            liTags.push(
                <li key={`current-${i}`} 
                    className={`${isToday ? 'active' : ''} ${hasEvent}`}
                    onClick={() => articlesByDate[dateKey] && openPdfViewer(articlesByDate[dateKey][0].pdfPath)}>
                    {i}
                </li>
            );
        }
        for (let i = lastDayofMonth; i < 7 && i !== 0; i++) {
            liTags.push(<li key={`next-${i}`} className="inactive">{i - lastDayofMonth + 1}</li>);
        }
        return liTags;
    };

    return (
        <main>
            <section className="hero fade-in">
                <h1>Les Actualités de l'AAMTDM</h1>
                <p>Découvrez les dernières actualités de l'Association des Amis du Musée des Troupes de Marine</p>
            </section>

            <section className="content-section fade-in">
                <h2>Introduction</h2>
                <p>Consultez notre agenda interactif et retrouvez l'historique de nos activités ci-dessous.</p>
            </section>

            <section className="agenda-feature-container fade-in">
                <div className="calendar-card">
                    <h2>Agenda</h2>
                    <div className="calendar-header">
                        <span className="current-date">{`${months[currMonth]} ${currYear}`}</span>
                        <div className="icons">
                            <span onClick={() => handleMonthChange(-1)}>&#10094;</span>
                            <span onClick={() => handleMonthChange(1)}>&#10095;</span>
                        </div>
                    </div>
                    <div className="calendar-body">
                        <ul className="days">{renderCalendar()}</ul>
                    </div>
                </div>

                <div className="feature-card">
                    <img src={conferencesImage} alt="Conférences 2025-2026" className="feature-img-cover" />
                    <div className="feature-button-area">
                        <button className="btn btn-primary" onClick={() => openPdfViewer(conferencesPdf)}>
                            Lire le programme des conférences
                        </button>
                    </div>
                </div>
            </section>

            <section className="content-section fade-in">
                <h2>Les actualités archivées</h2>
                <div id="archived-articles">
                    {sortedYears.map(year => (
                        <div key={year} className={`accordion-item ${openYear === year ? 'active' : ''}`}>
                            <div className={`accordion-header ${openYear === year ? 'active' : ''}`} onClick={() => setOpenYear(openYear === year ? null : year)}>
                                <h3>{year}</h3>
                            </div>
                            
                            <div className={`accordion-content ${openYear === year ? 'show' : ''}`}>
                                <div className="news-grid-container">
                                    <button className="arrow left-arrow" onClick={() => scrollNewsGrid(-1, `grid-${year}`)}>&#10094;</button>
                                    <div className="news-grid" id={`grid-${year}`}>
                                        {archivedByYear[year].map(article => (
                                            <div key={article.id} className="news-item">
                                                <img src={article.image} alt={article.title} />
                                                <h3>{article.title}</h3>
                                                <p><strong>{article.date}</strong></p>
                                                <p>{article.description}</p>
                                                <button className="btn btn-primary" onClick={() => openPdfViewer(article.pdfPath)}>Lire l'article</button>
                                            </div>
                                        ))}
                                    </div>
                                    <button className="arrow right-arrow" onClick={() => scrollNewsGrid(1, `grid-${year}`)}>&#10095;</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {!isMobile && pdfViewerVisible && selectedPdfPath && (
                <div className="pdf-viewer-overlay" onClick={closePdfViewer}>
                    <div className="pdf-viewer-container" onClick={(e) => e.stopPropagation()}>
                        <div className="pdf-viewer-header">
                            <button className="pdf-download-btn" onClick={() => window.open(selectedPdfPath)}>Télécharger</button>
                            <button className="close-pdf" onClick={closePdfViewer}>&times;</button>
                        </div>
                        <div className="pdf-content">
                            <iframe src={selectedPdfPath} className="pdf-viewer" title="PDF"></iframe>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
};

export default ActualitesAAMTDM;